import { useAtom } from "jotai";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { useAlert } from "@/hooks/useAlert";
import { useRequestTwoFactorAppSetup } from "@/hooks/useRequestTwoFactorAppSetup";
import { useValidateTwoFactorAppCode } from "@/hooks/useValidateTwoFactorAppCode";
import {
  useSetUpTwoFactorAuthentication,
  useValidateTwoFactorAuthentication,
} from "@/services/auth";
import { ValidateTwoFactorAuthenticationMethod } from "@/services/model";
import { AlertTypes, twoFactorBackupCodesAtom } from "@/utils/atoms";

import { AuthenticatorAppForm } from "./AuthenticatorAppForm";
import { TwoFactorConfirmationCodeForm } from "./TwoFactorConfirmationInput";

export const AuthenticatorAppSetup = ({
  logoutOnAuthError,
  navigationTarget,
}: {
  logoutOnAuthError: boolean;
  navigationTarget: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const showToast = useAlert();

  const { authSetupData, hasFailed } = useRequestTwoFactorAppSetup(
    useSetUpTwoFactorAuthentication,
    {
      method: ValidateTwoFactorAuthenticationMethod.APP,
    },
    true,
  );

  useEffect(() => {
    if (hasFailed) {
      showToast({
        text: t("translation:Common.generalForm.error.genericError"),
        type: AlertTypes.error,
      });
      navigate(-1);
    }
  }, [hasFailed, navigate, showToast, t]);

  const [, setBackupCodes] = useAtom(twoFactorBackupCodesAtom);
  // Form handling
  const formMethods = useForm<TwoFactorConfirmationCodeForm>({
    defaultValues: {
      code: "",
    },
    mode: "onChange",
  });

  const { isLoading, validateTwoFactorAuth } = useValidateTwoFactorAppCode(
    () => {
      formMethods.setError("code", {
        type: "manual",
        message: t("portal:authenticator.app.setup.step2.invalidCode"),
      });
    },
    logoutOnAuthError,
    useValidateTwoFactorAuthentication,
  );

  const onSubmit = (data: TwoFactorConfirmationCodeForm) => {
    validateTwoFactorAuth({
      params: {
        code: parseInt(data.code),
        method: ValidateTwoFactorAuthenticationMethod.APP,
      },
    }).then((data) => {
      if (data.backupCodes) {
        setBackupCodes(data.backupCodes);
        navigate(navigationTarget);
      }
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <ProfileCard
          maxWidth="none"
          title={t("portal:user-account.security.2fa.initialSetup.title")}
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => navigate(-1)}
              >
                {t("portal:user-account.security.buttons.cancel")}
              </Button>
              <SubmitButton
                fullWidth={false}
                size="sm"
                disabled={isLoading}
                label={t("portal:authenticator.sms.verification.finishSetup")}
              />
            </>
          }
        >
          <AuthenticatorAppForm authSetupData={authSetupData} />
        </ProfileCard>
      </form>
    </FormProvider>
  );
};
